/**
 * Convert an array of 'components' supplied by the Google Places API to the data we want to put into
 * an address model.
 * Google supplies various different 'types'. We need to decide what belongs where.
 *
 * @see https://developers.google.com/maps/documentation/places/web-service/details#AddressComponent
 * @see https://developers.google.com/maps/documentation/geocoding/requests-geocoding#Types
 *
 * Note that the order of the components returned by Google is not guaranteed. e.g. sometimes
 * administrative_area_level_2 comes before administrative_area_level_1. This function is written in such a
 * way that this does not cause a problem.
 *
 * @param {Array} components
 *
 * @return {{country: string, city: string, street: string, county: string, postcode: string, state: string, house:
 *     string}}
 */
const googleAddressComponentsToAddressData = (components) => {
    // Define which components should be used to fill each part of the address, in order of preference.
    const componentsForAddressParts = {
        // Country is first intentionally so we can fill it first and then do different things depending on
        // the country.
        country: ['country'],
        house: ['street_number', 'premise'],
        street: ['street_address', 'route'],
        town: [
            'locality',
            'postal_town',
            'administrative_area_level_3',
        ],
        county: [
            'administrative_area_level_1',
            'administrative_area_level_2',
            'administrative_area_level_3',
            'administrative_area_level_4',
            'administrative_area_level_5',
        ],
        state: [],
        postcode: ['postal_code'],
    };

    const address = {
        house: '',
        street: '',
        town: '',
        county: '',
        state: '',
        postcode: '',
        country: '',
    };

    /**
     * @param {string} type
     *
     * @return {?{long_name: string, short_name: string, types: string[]}}
     */
    const findComponentByType = (type) => {
        for (let i = 0; i < components.length; i++) {
            for (let t = 0; t < components[i].types.length; t++) {
                if (components[i].types[t] === type) {
                    return components[i];
                }
            }
        }

        return null;
    };

    for (let addressPartName in componentsForAddressParts) {
        /* istanbul ignore else */
        if (componentsForAddressParts.hasOwnProperty(addressPartName)) {
            const fillAddressPartFromComponents = componentsForAddressParts[addressPartName];

            for (let i = 0; i < fillAddressPartFromComponents.length; i++) {
                const component = findComponentByType(fillAddressPartFromComponents[i]);
                if (component) {
                    if (addressPartName === 'state') {
                        address[addressPartName] = component.short_name;
                    } else {
                        address[addressPartName] = component.long_name;
                    }

                    break;
                }
            }

            if (addressPartName === 'country') {
                switch (address.country) {
                    case 'United States':
                        // Special handling for US states.
                        // This is similar to how the Customer Facing site works. State is only shown for the US.
                        // Use administrative_area_level_1 for the state.
                        componentsForAddressParts.state = ['administrative_area_level_1'];
                        // Don't use administrative_area_level_1 for the county.
                        componentsForAddressParts.county.splice(0, 1);
                        break;
                    case 'United Kingdom':
                        // Don't use England/Scotland/Wales (administrative_area_level_1) for the county.
                        componentsForAddressParts.county.splice(0, 1);
                        break;
                }
            }
        }
    }

    return address;
};

module.exports = {
    googleAddressComponentsToAddressData,
};
